import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Loader2,
  X,
  Layers,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { Button, StatusBadge, statusLabel } from "../ui";
import { useToast } from "../ui/toast";

interface PhaseOut {
  id: string;
  project_id: string;
  name: string;
  position: number;
  created_at: string;
  updated_at: string;
}

interface TaskView {
  id: string;
  project_id: string | null;
  title: string;
  status: string | null;
  source: "local" | "salesforce";
  read_only: boolean;
  phase_id: string | null;
}

type Step = "basics" | "tasks" | "done";

const NAME_SUGGESTIONS = ["Konzept", "Analyse", "Entwicklung", "Pilot", "Rollout", "Test", "Review"];

export default function PhaseWizard({
  open,
  onClose,
  projectId,
  phase,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  phase?: PhaseOut | null;
  onSaved?: (p: PhaseOut) => void;
}) {
  const toast = useToast();
  const isEdit = !!phase;

  const [step, setStep] = useState<Step>("basics");
  const [name, setName] = useState("");
  const [tasks, setTasks] = useState<TaskView[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialSelected, setInitialSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Reset / Pre-fill bei open
  useEffect(() => {
    if (!open) return;
    setStep("basics");
    setName(phase?.name ?? "");
    setSelected(new Set());
    setInitialSelected(new Set());
    setTasks([]);
    setBusy(false);
  }, [open, phase?.id]);

  // Tasks laden sobald die Komponente offen ist: Lokale Tasks des Projekts ohne
  // Phase + Tasks, die bereits zur (Edit-) Phase gehören. SF-Tasks haben keine
  // Phase-Zuordnung — wir blenden sie aus.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingTasks(true);
    (async () => {
      try {
        const all = await api.get<TaskView[]>(
          `/views/tasks?project_id=${encodeURIComponent(projectId)}`
        );
        if (cancelled) return;
        const candidates = all.filter((t) => {
          if (t.source !== "local" || t.read_only) return false;
          if (t.phase_id === null) return true;
          if (isEdit && t.phase_id === phase!.id) return true;
          return false;
        });
        setTasks(candidates);
        const initSel = new Set(
          candidates.filter((t) => isEdit && t.phase_id === phase!.id).map((t) => t.id)
        );
        setSelected(new Set(initSel));
        setInitialSelected(initSel);
      } catch (e) {
        if (!cancelled) toast.err("Tasks konnten nicht geladen werden", (e as Error).message);
      } finally {
        if (!cancelled) setLoadingTasks(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, isEdit, phase?.id, toast]);

  const canNext = name.trim().length > 0;

  const toggleTask = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toAssign = useMemo(
    () => [...selected].filter((id) => !initialSelected.has(id)),
    [selected, initialSelected]
  );
  const toUnassign = useMemo(
    () => [...initialSelected].filter((id) => !selected.has(id)),
    [selected, initialSelected]
  );

  const save = async () => {
    if (!canNext) return;
    setBusy(true);
    try {
      let savedPhase: PhaseOut;
      if (isEdit) {
        savedPhase =
          name.trim() !== phase!.name
            ? await api.patch<PhaseOut>(`/phases/${phase!.id}`, { name: name.trim() })
            : phase!;
      } else {
        savedPhase = await api.post<PhaseOut>(
          `/projects/${projectId}/phases`,
          { name: name.trim() }
        );
      }
      if (toAssign.length > 0) {
        await api.post(`/phases/${savedPhase.id}/tasks/assign`, {
          task_ids: toAssign,
        });
      }
      for (const tid of toUnassign) {
        await api.patch(`/tasks/${tid}`, { phase_id: null });
      }
      setStep("done");
      onSaved?.(savedPhase);
    } catch (e) {
      toast.err("Workstream speichern fehlgeschlagen", (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-surface border border-border rounded-2xl shadow-2xl w-[640px] max-w-[92vw] max-h-[88vh] flex flex-col animate-modal-in">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <Layers className="w-4 h-4 text-brand-600" />
            <Dialog.Title className="font-semibold text-sm flex-1">
              {isEdit ? `Workstream „${phase!.name}" bearbeiten` : "Neuen Workstream anlegen"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="p-1 text-text-3 hover:text-text">
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>

          <Stepper step={step} />

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {step === "basics" && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-text mb-1.5 block">
                    Workstream-Name
                  </label>
                  <input
                    autoFocus
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && canNext) setStep("tasks");
                    }}
                    placeholder="z.B. Konzept, Pilot, Rollout …"
                    className="w-full px-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    maxLength={200}
                  />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-text-2 mb-1.5 inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Beispiele
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {NAME_SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setName(s)}
                        className="text-xs px-2 py-1 rounded-md bg-surface-3 hover:bg-brand-100 dark:hover:bg-brand-900/30 text-text hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === "tasks" && (
              <div className="space-y-3">
                <div className="text-sm text-text">
                  Welche bestehenden Tasks gehören zu diesem Workstream?
                </div>
                <div className="text-[11px] text-text-2">
                  Du siehst {isEdit ? "Tasks dieses Workstreams + freie Tasks" : "alle freien Tasks"}{" "}
                  des Projekts. SF-Tasks sind read-only und nicht zuordenbar.
                </div>
                {loadingTasks ? (
                  <div className="flex items-center gap-2 text-xs text-text-2 py-4">
                    <Loader2 className="w-3 h-3 animate-spin" /> Tasks werden geladen …
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="text-xs text-text-2 italic py-4">
                    Keine zuordenbaren Tasks im Projekt. Du kannst den Workstream trotzdem
                    anlegen und Tasks später zuordnen.
                  </div>
                ) : (
                  <ul className="border border-border rounded-md divide-y divide-border max-h-[340px] overflow-y-auto">
                    {tasks.map((t) => (
                      <li key={t.id}>
                        <label className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-surface-2">
                          <input
                            type="checkbox"
                            checked={selected.has(t.id)}
                            onChange={() => toggleTask(t.id)}
                            className="accent-brand-600 w-4 h-4"
                          />
                          <span className="flex-1 text-sm truncate">{t.title}</span>
                          <StatusPill status={t.status} />
                          {isEdit && t.phase_id === phase!.id && (
                            <span className="text-[10px] text-brand-600 dark:text-brand-400 uppercase tracking-wider">
                              im Workstream
                            </span>
                          )}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                {(toAssign.length > 0 || toUnassign.length > 0) && (
                  <div className="text-[11px] text-text-2">
                    {toAssign.length > 0 && <span>+{toAssign.length} hinzu </span>}
                    {toUnassign.length > 0 && <span>−{toUnassign.length} entfernen</span>}
                  </div>
                )}
              </div>
            )}

            {step === "done" && (
              <div className="py-8 flex flex-col items-center text-center space-y-3">
                <CheckCircle2 className="w-12 h-12 text-success" />
                <div className="text-sm font-medium">
                  Workstream „{name}" {isEdit ? "aktualisiert" : "angelegt"}.
                </div>
                {(toAssign.length > 0 || toUnassign.length > 0) && (
                  <div className="text-xs text-text-2">
                    {toAssign.length > 0 && (
                      <span>{toAssign.length} Task(s) zugewiesen. </span>
                    )}
                    {toUnassign.length > 0 && (
                      <span>{toUnassign.length} Task(s) entfernt.</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2">
            <div>
              {step === "tasks" && (
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => setStep("basics")}
                  icon={<ArrowLeft className="w-3.5 h-3.5" />}
                >
                  Zurück
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {step === "basics" && (
                <>
                  <Button variant="ghost" size="md" onClick={onClose}>
                    Abbrechen
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => setStep("tasks")}
                    disabled={!canNext}
                  >
                    Weiter <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
              {step === "tasks" && (
                <Button
                  variant="primary"
                  size="md"
                  onClick={save}
                  disabled={!canNext || busy}
                  loading={busy}
                >
                  {isEdit ? "Speichern" : "Workstream anlegen"}
                </Button>
              )}
              {step === "done" && (
                <Button variant="primary" size="md" onClick={onClose}>
                  Schließen
                </Button>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Stepper({ step }: { step: Step }) {
  const stages: { id: Step; label: string }[] = [
    { id: "basics", label: "Name" },
    { id: "tasks", label: "Aufgaben" },
    { id: "done", label: "Fertig" },
  ];
  const idx = stages.findIndex((s) => s.id === step);
  return (
    <div className="px-5 py-2 border-b border-border flex items-center gap-1.5">
      {stages.map((s, i) => (
        <div key={s.id} className="flex items-center gap-1.5">
          <div
            className={clsx(
              "w-5 h-5 rounded-full grid place-items-center text-[10px] font-medium",
              i < idx
                ? "bg-success text-white"
                : i === idx
                  ? "bg-brand-600 text-white"
                  : "bg-surface-3 text-text-2"
            )}
          >
            {i + 1}
          </div>
          <span
            className={clsx(
              "text-[11px]",
              i === idx
                ? "text-text dark:text-white font-medium"
                : "text-text-2"
            )}
          >
            {s.label}
          </span>
          {i < stages.length - 1 && (
            <div className="w-4 h-px bg-surface-3 mx-0.5" />
          )}
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }: { status: string | null }) {
  if (!status) return null;
  const info = statusLabel(status);
  return <StatusBadge status={info.status}>{info.label}</StatusBadge>;
}
